import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { Avatar, AvatarFallback } from '@/modern/components/ui/avatar';
import { Badge } from '@/modern/components/ui/badge';
import { X, Clock, Calendar, User, Repeat } from 'lucide-react';
import { format } from 'date-fns';
import { Reminder } from '@/bryntum-lib/models/Reminder';
import { NOTIFICATION_STATUS } from '@/other/constants/OtherKeys';

interface ReminderDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reminder: Reminder;
  notifyUsers?: string[];
}

const extractInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

const ReminderDetailModal: React.FC<ReminderDetailModalProps> = ({
  open,
  onOpenChange,
  reminder,
  notifyUsers = []
}) => {
  // Compute display values from Reminder model
  const displayData = useMemo(() => {
    const recurrenceMap: { [key: number]: string } = {
      0: "Never",
      1: "Daily",
      2: "Weekly",
      3: "Monthly",
      4: "Yearly"
    };

    const entityTypeMap: { [key: number]: string } = {
      1: "Resource",
      2: "Client",
      3: "Task"
    };

    const isRecurring = Boolean(reminder.recurringInterval);
    const recurrence = recurrenceMap[reminder.recurringInterval as number] || "Never";
    const entityTypeName = entityTypeMap[reminder.entityType as number] || "Entity";
    const entityName = reminder.entity?.name || "Unknown";
    const status = reminder.status === NOTIFICATION_STATUS.SENT ? "Sent" :
                   reminder.status === NOTIFICATION_STATUS.FAILED ? "Failed" : "Pending";
    const creator = reminder.creatorBoardUserRecord?.name || "N/A";

    return {
      isRecurring,
      recurrence,
      entityTypeName,
      entityName,
      status,
      creator
    };
  }, [reminder]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 pt-6 pb-2 sticky top-0 bg-background z-10 border-0">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base font-normal break-words min-w-0 flex-1">
              {reminder.title || reminder.description || 'Reminder'}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-muted shrink-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-6 space-y-4">
          {reminder.description && (
            <p className="text-sm text-muted-foreground mb-3 break-words">
              {reminder.description}
            </p>
          )}

          <div className="space-y-2">
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-foreground">
                  {format(new Date(reminder.alertDate as Date), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  {displayData.entityTypeName} - {displayData.entityName}
                </span>
              </div>

              {displayData.isRecurring && (
                <div className="flex items-center gap-2 text-sm">
                  <Repeat className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Recurring {displayData.recurrence}
                    {reminder.repeatCount && ` (${reminder.repeatCount} time${reminder.repeatCount > 1 ? 's' : ''})`}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  Status: {displayData.status}
                </span>
              </div>

              {reminder.remaining > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">
                    {reminder.remaining} days remaining
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  Created by: {displayData.creator}
                </span>
              </div>
            </div>

            {/* Notify Users */}
            {notifyUsers && notifyUsers.length > 0 && (
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-normal text-foreground">
                    Notify Users
                  </h4>
                  <Badge variant="secondary" className="h-5 px-2 text-xs">
                    {notifyUsers.length}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-y-4 gap-x-3">
                  {notifyUsers.map((user, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                          {extractInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground">{user}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderDetailModal;
